'use client'

import React from 'react'
import { Shield, Info, FileText, BarChart3, Scale, Share2, Eye, ShieldCheck, Archive, Settings, Mail, CheckCircle, Link as LinkIcon } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaGooglePlay, FaApple } from 'react-icons/fa'

const sections = [
  {
    id: 1,
    title: '1. Introduction',
    content: 'We respect your privacy and are committed to protecting your personal information.\n\nThis Privacy Policy outlines how we handle data when you:\n\n• Use the Abhiram Edu Info Tech Website and the applications\n• Create an account\n• Access learning content\n• Communicate with our support team\n• Participate in activities, surveys, or promotions\n\nBy using the app, you agree to the practices described in this policy.',
    iconName: 'Info'
  },
  {
    id: 2,
    title: '2. Information We Collect',
    content: 'A. Personal Information (Provided by You)\n\nWe may collect information that you voluntarily provide when you:\n\n• Create or update an account\n• Register student\'s profile\n• Contact support\n• Participate in promotions\n\nThis may include:\n\n• Name of Student\n• Age (if applicable)\n• Mobile number\n• Email address\n• Profile photo (optional)\n• Login credentials\n• Communication and support history\n\nB. Automatically Collected Information\n\nWhen you use the app, we automatically collect:\n\n• Device information (model, OS version, device identifiers)\n• IP address\n• App usage data (screens visited, time spent, interactions)\n• Crash logs and performance data\n• Approximate location (if permitted)\n• Cookies or similar tracking technologies\n\nC. Business or Institutional Information (If Applicable)\n\nFor schools, partners, or distributors, we may also collect:\n\n• Organization name\n• Contact person details\n• GST number (if applicable)\n• Purchase and subscription details',
    iconName: 'FileText'
  },
  {
    id: 3,
    title: '3. How We Use Your Information',
    content: 'We use collected data to:\n\n• Manage user accounts\n• Provide educational content and app features\n• Improve learning experience\n• Communicate updates, alerts, and support responses\n• Process subscriptions or payments\n• Enhance app performance and security\n• Prevent fraud or misuse\n• Comply with legal obligations\n• Analyze usage to improve our product',
    iconName: 'BarChart3'
  },
  {
    id: 4,
    title: '4. Legal Basis for Processing (India)',
    content: 'We process your information based on:\n\n• Contractual necessity: to provide app services\n• Legitimate interest: app improvement, security\n• Consent: notifications, marketing communication\n• Legal obligations: government, tax, or compliance requirements',
    iconName: 'Scale'
  },
  {
    id: 5,
    title: '5. Sharing Your Information',
    content: 'We do not sell personal information.\n\nWe may share limited information with:\n\nA. Service Providers\n\nSuch as:\n\n• Cloud hosting providers\n• Payment gateways\n• Analytics tools\n• Notification service providers\n• Customer support platforms\n\nAll operate under strict confidentiality and data protection requirements.\n\nB. Legal Compliance\n\nWe may disclose information when required by:\n\n• Law\n• Court order\n• Government request\n• To protect our rights or user safety\n\nC. Business Transfers\n\nIn case of merger, acquisition, or restructuring, user information may be transferred to the new entity.',
    iconName: 'Share2'
  },
  {
    id: 6,
    title: '6. Cookies & Tracking Technologies',
    content: 'The app may use:\n\n• Essential cookies\n• Analytics tools\n• Performance monitoring tools\n\nYou can manage permissions in device settings. Disabling certain permissions may affect app functionality.',
    iconName: 'Eye'
  },
  {
    id: 7,
    title: '7. Data Security',
    content: 'We use appropriate technical and organizational safeguards, including:\n\n• Encrypted data transmission\n• Secure authentication\n• Restricted access\n• Regular security audits\n• Secure payment processing\n\nHowever, no method is completely secure, and we cannot guarantee absolute safety.',
    iconName: 'ShieldCheck'
  },
  {
    id: 8,
    title: '8. Data Retention',
    content: 'We retain information only as long as necessary for:\n\n• Account and service management\n• Compliance with legal obligations\n• Resolving disputes\n• Security and fraud prevention\n\nUser and transaction data may be stored for up to 7 years as required by law.',
    iconName: 'Archive'
  },
  {
    id: 9,
    title: '9. Your Rights',
    content: 'Depending on applicable laws, you may:\n\n• Access your information\n• Request corrections\n• Request deletion (where legally permitted)\n• Withdraw consent (notifications, marketing)\n• Object to certain processing\n• Request data portability\n\nTo exercise rights, contact: info@abhirameduinfotech.com',
    iconName: 'Settings'
  },
  {
    id: 10,
    title: '10. Third-Party Links',
    content: 'The app may display links to external websites or services.\n\nWe are not responsible for the privacy practices of third parties.',
    iconName: 'LinkIcon'
  },
  {
    id: 11,
    title: '11. Marketing Communications',
    content: 'With your consent, we may send updates about:\n\n• New features\n• Educational content\n• Offers and promotions\n\nYou may opt out at any time via:\n\n• App settings\n• "Unsubscribe" link\n• Email request',
    iconName: 'Mail'
  },
]

export default function PrivacyPolicy() {
  const router = useRouter()

  const getIcon = (iconName: string) => {
    const icons: { [key: string]: any } = {
      Info,
      FileText,
      BarChart3,
      Scale,
      Share2,
      Eye,
      ShieldCheck,
      Archive,
      Settings,
      LinkIcon,
      Mail
    }
    return icons[iconName] || Info
  }

  // Navigate to homepage and scroll to section
  const navigateToHomeSection = (sectionId: string) => {
    // Navigate to homepage with hash for the section
    router.push(`/#${sectionId}`)
  }

  const renderContent = (content: string) => {
    return content.split('\n').map((line, idx) => {
      if (line.trim().startsWith('•')) {
        return (
          <div key={idx} className="flex items-start space-x-2 mb-1">
            <span className="text-orange-500 font-bold text-base mt-0.5">•</span>
            <span className="text-gray-700 leading-relaxed text-sm">{line.substring(1).trim()}</span>
          </div>
        )
      } else if (line.trim() === '') {
        return <div key={idx} className="h-1" />
      } else if (line.match(/^[A-Z]\.\s/)) {
        const parts = line.split(':')
        return (
          <div key={idx} className="mb-2 mt-3">
            <span className="font-semibold text-gray-900 text-base">{parts[0]}:</span>
            {parts[1] && <span className="text-gray-700 ml-1 text-sm">{parts[1]}</span>}
          </div>
        )
      } else {
        return (
          <p key={idx} className="text-gray-700 leading-relaxed mb-1 text-sm">
            {line}
          </p>
        )
      }
    })
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 custom-font'>
      {/* Navbar */}
      <nav className=' px-8 py-4 shadow-sm bg-white sticky top-0 z-50'>
        <div className='max-w-6xl mx-auto flex items-center justify-between'>
          <div className='flex items-center'>
            <Image
              src='/ScoreEZE_Logo.png'
              alt='SCOREEZE Logo'
              width={120}
              height={40}
              className='h-16 w-auto'
            />
          </div>
          <ul className='hidden md:flex items-center space-x-8 text-gray-700 font-medium'>
            <li className='hover:text-cyan-600 cursor-pointer' onClick={() => navigateToHomeSection('home')}>Home</li>
            <li className='hover:text-cyan-600 cursor-pointer' onClick={() => navigateToHomeSection('about')}>About</li>
            <li className='hover:text-cyan-600 cursor-pointer' onClick={() => navigateToHomeSection('courses')}>Courses</li>
            <li className='hover:text-cyan-600 cursor-pointer' onClick={() => navigateToHomeSection('why-choose-us')}>Why Us?</li>
          </ul>
          <div className='flex items-center gap-3'>
            <Link href='/login' className='bg-cyan-600 text-white hover:bg-cyan-700 px-4 py-2 rounded-sm' >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-4xl font-bold text-blue-900 mb-8 text-center max-w-md mx-auto">Privacy Policy</h2>
        {/* Introduction Notice */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5 mb-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Info className="text-blue-600" size={24} />
            </div>
            <div>

              <p className="text-blue-800 leading-relaxed">
                This Privacy Policy shall govern the Contractual and Statutory Relationship between the Abhiram Edu Info Tech Website and the software applications used by it (Referred to as "Abhiram" and the User(s). This Privacy Policy and Terms of Use must be read and all these Legal Documents shall collectively govern the relationship and use of this Website and its Services by the User(s).
              </p>
            </div>
          </div>
        </div>

        {/* Privacy Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-6 py-3 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                    {React.createElement(getIcon(section.iconName), { className: "text-white", size: 16 })}
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
                </div>
              </div>
              <div className="px-6 py-4">
                <div className="prose prose-gray max-w-none">
                  {renderContent(section.content)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-2xl p-5 mt-6">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Mail className="text-gray-600" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 mb-1">Privacy Questions?</h3>
              <p className="text-gray-600 mb-3 text-sm">
                If you have questions about our privacy practices or want to exercise your rights, contact our privacy team.
              </p>
              <a
                href="mailto:info@abhirameduinfotech.com"
                className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-sm"
              >
                <Mail size={16} />
                <span className="font-medium">Contact Privacy Team</span>
              </a>
            </div>
          </div>
        </div>

        {/* Agreement Notice */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-5 mt-4">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="text-green-600" size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-green-900 mb-1">We Protect Your Privacy</h3>
              <p className="text-green-800 leading-relaxed text-sm">
                Your trust is important to us. We are committed to protecting your personal information and being transparent about our data practices.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className='bg-gray-800 border-t border-gray-700 mt-20 relative overflow-hidden'>
        {/* Background Pattern */}
        <div className='absolute inset-0 opacity-5'>
          <div className='absolute top-10 left-10 w-32 h-32 bg-orange-400 rounded-full blur-3xl'></div>
          <div className='absolute bottom-10 right-10 w-40 h-40 bg-cyan-400 rounded-full blur-3xl'></div>
          <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-purple-400 rounded-full blur-3xl'></div>
        </div>

        <div className='max-w-7xl mx-auto px-6 py-12 relative'>
          <div className='grid md:grid-cols-12 gap-8 mb-8'>
            {/* Brand Section */}
            <div className='md:col-span-4 space-y-4'>
              <div className='flex items-center space-x-3'>
                <Image
                  src='/ScoreEZE_Logo.png'
                  alt='SCOREEZE Logo'
                  width={120}
                  height={40}
                  className='h-12 w-auto'
                />
              </div>
              <p className='text-gray-300 text-sm leading-relaxed'>
                Empowering students from 5th to 10th grade with interactive learning through videos, quizzes, and comprehensive study materials.
              </p>
              <div className='flex space-x-3 pt-2'>
                <a href='https://facebook.com/scoreeze' target='_blank' rel='noopener noreferrer'
                  className='w-11 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-110'>
                  <FaFacebookF />
                </a>
                <a href='https://twitter.com/scoreeze' target='_blank' rel='noopener noreferrer'
                  className='w-11 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-110'>
                  <FaTwitter />
                </a>
                <a href='https://instagram.com/scoreeze' target='_blank' rel='noopener noreferrer'
                  className='w-11 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-110'>
                  <FaInstagram />
                </a>
                <a href='https://linkedin.com/company/scoreeze' target='_blank' rel='noopener noreferrer'
                  className='w-11 h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-110'>
                  <FaLinkedinIn />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className='md:col-span-2 space-y-4'>
              <h4 className='text-sm font-bold text-white uppercase tracking-wider border-b-2 border-orange-400 pb-1 w-fit'>Quick Links</h4>
              <ul className='space-y-3'>
                <li>
                  <button onClick={() => navigateToHomeSection('about')}
                    className='text-gray-300 hover:text-orange-400 text-sm transition-all duration-300 hover:translate-x-1 font-medium'>
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => navigateToHomeSection('courses')}
                    className='text-gray-300 hover:text-orange-400 text-sm transition-all duration-300 hover:translate-x-1 font-medium'>
                    Courses
                  </button>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div className='md:col-span-2 space-y-4'>
              <h4 className='text-sm font-bold text-white uppercase tracking-wider border-b-2 border-cyan-400 pb-1 w-fit'>Support</h4>
              <ul className='space-y-3'>
                <li>
                  <a href='/privacy-policy'
                    className='text-gray-300 hover:text-cyan-400 text-sm transition-all duration-300 hover:translate-x-1 font-medium'>
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href='/terms-of-service'
                    className='text-gray-300 hover:text-cyan-400 text-sm transition-all duration-300 hover:translate-x-1 font-medium'>
                    Terms & Conditions
                  </a>
                </li>
              </ul>
            </div>

            {/* App Download */}
            <div className='md:col-span-4 space-y-4'>
              <h4 className='text-sm font-bold text-white uppercase tracking-wider border-b-2 border-purple-400 pb-1 w-fit'>Download App</h4>
              <p className='text-gray-300 text-sm mb-4 font-medium'>Get the SCOREEZE app for the best learning experience</p>
              <div className='flex flex-col sm:flex-row gap-3'>
                <a href='https://play.google.com/store/apps/details?id=com.scoreeze' target='_blank' rel='noopener noreferrer'
                  className='flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-xl transition-all duration-300 gap-3 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105'>
                  <FaGooglePlay className='text-lg' />
                  <span>Google Play</span>
                </a>
                <a href='https://apps.apple.com/app/scoreeze/id123456789' target='_blank' rel='noopener noreferrer'
                  className='flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-xl transition-all duration-300 gap-3 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105'>
                  <FaApple className='text-lg' />
                  <span>App Store</span>
                </a>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className='border-t border-slate-300 my-8 relative'>
            <div className='absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400 to-transparent'></div>
          </div>

          {/* Bottom Section */}
          <div className='flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0'>
            <div className='text-gray-400 text-sm font-medium'>
              © {new Date().getFullYear()} SCOREEZE. All rights reserved.
            </div>
            <div className='flex items-center space-x-2 text-sm text-gray-400 font-medium'>
              <span>Made with</span>
              <span className='text-red-500 animate-pulse'>❤️</span>
              <span>for better education</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}